import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { Stakeholder, StakeholderOrder } from '../../types/stakeholder.types';

export const useStakeholdersWithShares = ({
  companyId,
  conversionEligible = false,
  enabled = true,
  numberOfFetchedItems = 50,
  search,
  sortOrder = StakeholderOrder.ASC,
}: {
  companyId: string;
  conversionEligible?: boolean;
  enabled?: boolean;
  numberOfFetchedItems?: number;
  search: string;
  sortOrder?: StakeholderOrder;
}) => {
  const {
    data: stakeholdersData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery<{
    stakeholders: Stakeholder[];
    totalPages: number;
  }>({
    queryKey: [
      QueryKey.GET_STAKEHOLDERS_WITH_SHARES,
      companyId,
      {
        search,
        conversionEligible,
        sortOrder,
      },
    ],
    queryFn: ({ pageParam = 0 }) => {
      return ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'stakeholders-with-shares'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip: pageParam,
            search,
            conversionEligible,
            sortOrder,
          },
        },
      );
    },
    enabled: !!companyId && enabled,
    initialPageParam: 0,
    refetchOnReconnect: true,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.totalPages > allPages.length ? allPages.length * numberOfFetchedItems : undefined,
  });
  const stakeholders = useMemo(
    () =>
      stakeholdersData?.pages?.reduce<Stakeholder[]>((acc, page) => {
        return [...acc, ...page.stakeholders];
      }, []),
    [stakeholdersData?.pages],
  );

  return {
    stakeholders: stakeholders || [],
    hasNextPage,
    fetchNextPage,
    stakeholdersData,
    isEmpty: stakeholders?.length === 0,
    numberOfFetchedItems,
    isLoading,
  };
};
